/**
 * FROZEN legacy admin-1 tables for the five shipped storefront countries.
 *
 * These are NOT the canonical source — `admin1-registry.ts` is. They exist so
 * the two country-less legacy helpers (`frontend/lib/region-names.ts` and
 * `utils/get-region-name.ts`) keep producing byte-identical output while their
 * callers migrate: the registry uses ISO 3166-2 codes for France (IDF, ARA…)
 * and the UK (ENG, SCT…), whereas these tables key those two countries by the
 * region NAME, and that key is what is persisted in `UserLocation.region` and
 * shipped to the AI prompts today. Swapping them would silently rewrite live
 * user preferences and prompt copy.
 *
 * Do not add countries here. New countries go to the registry.
 */

export interface RegionMapping {
  name: string;
  country: string;
}

/** Canadian provinces, 2-letter codes. */
export const LEGACY_CANADIAN_REGIONS: Record<string, RegionMapping> = {
  QC: { name: "Quebec", country: "Canada" },
  ON: { name: "Ontario", country: "Canada" },
  BC: { name: "British Columbia", country: "Canada" },
  AB: { name: "Alberta", country: "Canada" },
  MB: { name: "Manitoba", country: "Canada" },
  SK: { name: "Saskatchewan", country: "Canada" },
  NS: { name: "Nova Scotia", country: "Canada" },
  NB: { name: "New Brunswick", country: "Canada" },
  NL: { name: "Newfoundland and Labrador", country: "Canada" },
  PE: { name: "Prince Edward Island", country: "Canada" },
  NT: { name: "Northwest Territories", country: "Canada" },
  YT: { name: "Yukon", country: "Canada" },
  NU: { name: "Nunavut", country: "Canada" },
};

/**
 * Statistics Canada PRUID codes, as they appear in the province GeoJSON.
 * Note CA12 says "Yukon Territory" where the 2-letter YT says "Yukon" — a
 * pre-existing inconsistency, preserved deliberately.
 */
export const LEGACY_CANADIAN_PRUID_REGIONS: Record<string, RegionMapping> = {
  CA01: { name: "Alberta", country: "Canada" },
  CA02: { name: "British Columbia", country: "Canada" },
  CA03: { name: "Manitoba", country: "Canada" },
  CA04: { name: "New Brunswick", country: "Canada" },
  CA05: { name: "Newfoundland and Labrador", country: "Canada" },
  CA06: { name: "Northwest Territories", country: "Canada" },
  CA07: { name: "Nova Scotia", country: "Canada" },
  CA08: { name: "Ontario", country: "Canada" },
  CA09: { name: "Prince Edward Island", country: "Canada" },
  CA10: { name: "Quebec", country: "Canada" },
  CA11: { name: "Saskatchewan", country: "Canada" },
  CA12: { name: "Yukon Territory", country: "Canada" },
  CA13: { name: "Nunavut", country: "Canada" },
};

/** US states + DC, 2-letter codes. */
export const LEGACY_US_REGIONS: Record<string, RegionMapping> = {
  AL: { name: "Alabama", country: "United States" },
  AK: { name: "Alaska", country: "United States" },
  AZ: { name: "Arizona", country: "United States" },
  AR: { name: "Arkansas", country: "United States" },
  CA: { name: "California", country: "United States" },
  CO: { name: "Colorado", country: "United States" },
  CT: { name: "Connecticut", country: "United States" },
  DE: { name: "Delaware", country: "United States" },
  FL: { name: "Florida", country: "United States" },
  GA: { name: "Georgia", country: "United States" },
  HI: { name: "Hawaii", country: "United States" },
  ID: { name: "Idaho", country: "United States" },
  IL: { name: "Illinois", country: "United States" },
  IN: { name: "Indiana", country: "United States" },
  IA: { name: "Iowa", country: "United States" },
  KS: { name: "Kansas", country: "United States" },
  KY: { name: "Kentucky", country: "United States" },
  LA: { name: "Louisiana", country: "United States" },
  ME: { name: "Maine", country: "United States" },
  MD: { name: "Maryland", country: "United States" },
  MA: { name: "Massachusetts", country: "United States" },
  MI: { name: "Michigan", country: "United States" },
  MN: { name: "Minnesota", country: "United States" },
  MS: { name: "Mississippi", country: "United States" },
  MO: { name: "Missouri", country: "United States" },
  MT: { name: "Montana", country: "United States" },
  NE: { name: "Nebraska", country: "United States" },
  NV: { name: "Nevada", country: "United States" },
  NH: { name: "New Hampshire", country: "United States" },
  NJ: { name: "New Jersey", country: "United States" },
  NM: { name: "New Mexico", country: "United States" },
  NY: { name: "New York", country: "United States" },
  NC: { name: "North Carolina", country: "United States" },
  ND: { name: "North Dakota", country: "United States" },
  OH: { name: "Ohio", country: "United States" },
  OK: { name: "Oklahoma", country: "United States" },
  OR: { name: "Oregon", country: "United States" },
  PA: { name: "Pennsylvania", country: "United States" },
  RI: { name: "Rhode Island", country: "United States" },
  SC: { name: "South Carolina", country: "United States" },
  SD: { name: "South Dakota", country: "United States" },
  TN: { name: "Tennessee", country: "United States" },
  TX: { name: "Texas", country: "United States" },
  UT: { name: "Utah", country: "United States" },
  VT: { name: "Vermont", country: "United States" },
  VA: { name: "Virginia", country: "United States" },
  WA: { name: "Washington", country: "United States" },
  WV: { name: "West Virginia", country: "United States" },
  WI: { name: "Wisconsin", country: "United States" },
  WY: { name: "Wyoming", country: "United States" },
  DC: { name: "District of Columbia", country: "United States" },
};

/** French regions, keyed by name (metropolitan only). */
export const LEGACY_FRENCH_REGIONS: Record<string, RegionMapping> = {
  "Île-de-France": { name: "Île-de-France", country: "France" },
  "Auvergne-Rhône-Alpes": { name: "Auvergne-Rhône-Alpes", country: "France" },
  "Provence-Alpes-Côte d'Azur": { name: "Provence-Alpes-Côte d'Azur", country: "France" },
  Occitanie: { name: "Occitanie", country: "France" },
  "Nouvelle-Aquitaine": { name: "Nouvelle-Aquitaine", country: "France" },
  "Hauts-de-France": { name: "Hauts-de-France", country: "France" },
  Normandie: { name: "Normandie", country: "France" },
  "Grand Est": { name: "Grand Est", country: "France" },
  Bretagne: { name: "Bretagne", country: "France" },
  "Pays de la Loire": { name: "Pays de la Loire", country: "France" },
  "Centre-Val de Loire": { name: "Centre-Val de Loire", country: "France" },
  "Bourgogne-Franche-Comté": { name: "Bourgogne-Franche-Comté", country: "France" },
  Corse: { name: "Corse", country: "France" },
};

/** UK nations + English statistical regions, keyed by name. */
export const LEGACY_UK_REGIONS: Record<string, RegionMapping> = {
  England: { name: "England", country: "United Kingdom" },
  Scotland: { name: "Scotland", country: "United Kingdom" },
  Wales: { name: "Wales", country: "United Kingdom" },
  "Northern Ireland": { name: "Northern Ireland", country: "United Kingdom" },
  London: { name: "London", country: "United Kingdom" },
  "South East": { name: "South East", country: "United Kingdom" },
  "South West": { name: "South West", country: "United Kingdom" },
  "East of England": { name: "East of England", country: "United Kingdom" },
  "West Midlands": { name: "West Midlands", country: "United Kingdom" },
  "East Midlands": { name: "East Midlands", country: "United Kingdom" },
  "North West": { name: "North West", country: "United Kingdom" },
  "North East": { name: "North East", country: "United Kingdom" },
  "Yorkshire and the Humber": { name: "Yorkshire and the Humber", country: "United Kingdom" },
};

/**
 * Australian states/territories. Keyed by both the short code and the full
 * name; the full-name duplicates are what make a country-less merged map
 * resolve "Victoria" at all, so they stay.
 */
export const LEGACY_AUSTRALIAN_REGIONS: Record<string, RegionMapping> = {
  NSW: { name: "New South Wales", country: "Australia" },
  VIC: { name: "Victoria", country: "Australia" },
  QLD: { name: "Queensland", country: "Australia" },
  WA: { name: "Western Australia", country: "Australia" },
  SA: { name: "South Australia", country: "Australia" },
  TAS: { name: "Tasmania", country: "Australia" },
  ACT: { name: "Australian Capital Territory", country: "Australia" },
  NT: { name: "Northern Territory", country: "Australia" },
  "New South Wales": { name: "New South Wales", country: "Australia" },
  Victoria: { name: "Victoria", country: "Australia" },
  Queensland: { name: "Queensland", country: "Australia" },
  "Western Australia": { name: "Western Australia", country: "Australia" },
  "South Australia": { name: "South Australia", country: "Australia" },
  Tasmania: { name: "Tasmania", country: "Australia" },
  "Australian Capital Territory": { name: "Australian Capital Territory", country: "Australia" },
  "Northern Territory": { name: "Northern Territory", country: "Australia" },
};
